import { gql } from '@apollo/client';
import { useOffsetPaginatedQuery } from '@everlutionsk/ui-apollo';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Button } from '../components/coreComponents/button';
import { Input } from '../components/coreComponents/input';
import { SelectInput, SelectOption } from '../components/coreComponents/selectInput';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { Table } from '../components/microLayouts/table';
import { AdminAuditAction, AdminAuditPhase } from '../graphql/types';

// Human label per action — keys match the AdminAuditAction enum in the admin
// schema. Falls back to the raw key if a new action is added before this map.
const ACTION_LABEL: Record<AdminAuditAction, string> = {
  accountProvisioned: 'Account provisioned',
  accountPublished: 'Account published',
  accountPermissionsUpdated: 'Permissions updated',
  accountBrandingUpdated: 'Branding updated',
  accountDefaultsSeeded: 'Defaults seeded',
  accountRestored: 'Account restored',
  accountDeleteRequested: 'Deletion requested',
  accountDeleted: 'Account deleted',
  accountBackupPurgeRequested: 'Backup purge requested',
  accountBackupPurged: 'Backup purged',
  notificationSettingsUpdated: 'Notification settings',
  notificationRuleUpdated: 'Notification rule',
  adminInvited: 'Administrator invited',
  adminUpdated: 'Administrator updated',
  adminCredentialsRequested: 'Admin credentials requested',
  adminCredentialsCreated: 'Admin credentials created',
  adminCredentialsRevokeRequested: 'Admin access revoke requested',
  adminCredentialsRevoked: 'Admin access revoked',
  pendingActionRejected: 'Pending action rejected'
};

const ALL = 'all';

// Relative-time presets, mirroring the CloudWatch range picker. `null` minutes
// means "all time" (no `since` bound). `key` is the select value.
const RANGE_OPTIONS: ReadonlyArray<{
  readonly key: string;
  readonly label: string;
  readonly minutes: number | null;
}> = [
  { key: '5m', label: 'Last 5 minutes', minutes: 5 },
  { key: '15m', label: 'Last 15 minutes', minutes: 15 },
  { key: '30m', label: 'Last 30 minutes', minutes: 30 },
  { key: '1h', label: 'Last 1 hour', minutes: 60 },
  { key: '3h', label: 'Last 3 hours', minutes: 180 },
  { key: '12h', label: 'Last 12 hours', minutes: 720 },
  { key: '24h', label: 'Last 24 hours', minutes: 1440 },
  { key: '7d', label: 'Last 7 days', minutes: 10080 },
  { key: 'all', label: 'All time', minutes: null }
];

const rangeOptions: ReadonlyArray<SelectOption<string>> = RANGE_OPTIONS.map(option => ({
  label: option.label,
  value: option.key
}));

const actionOptions: ReadonlyArray<SelectOption<typeof ALL | AdminAuditAction>> = [
  { label: 'All actions', value: ALL },
  ...(Object.keys(ACTION_LABEL) as AdminAuditAction[]).map(key => ({
    label: ACTION_LABEL[key],
    value: key
  }))
];

const phaseOptions: ReadonlyArray<SelectOption<typeof ALL | AdminAuditPhase>> = [
  { label: 'All phases', value: ALL },
  { label: 'Triggered', value: 'triggered' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' }
];

// Min-duration presets — a select instead of a free number field. `0` means
// "any" (no `minDurationMs` filter); the rest are lower bounds in ms.
const minDurationOptions: ReadonlyArray<SelectOption<number>> = [
  { label: 'Any duration', value: 0 },
  { label: '≥ 500 ms', value: 500 },
  { label: '≥ 1 second', value: 1000 },
  { label: '≥ 5 seconds', value: 5000 },
  { label: '≥ 30 seconds', value: 30000 },
  { label: '≥ 1 minute', value: 60000 }
];

export function Logs() {
  const [rangeKey, setRangeKey] = useState<string>('24h');
  const [action, setAction] = useState<typeof ALL | AdminAuditAction>(ALL);
  const [phase, setPhase] = useState<typeof ALL | AdminAuditPhase>(ALL);
  // `accountDraft` is the live input; `account` is the committed filter (applied
  // on Enter/blur) so we don't refetch + reset pagination on every keystroke.
  const [accountDraft, setAccountDraft] = useState('');
  const [account, setAccount] = useState('');
  // 0 = "any duration" (no filter); other values are lower bounds in ms.
  const [minDurationMs, setMinDurationMs] = useState(0);
  // Bumped by Refresh so the relative `since` window is recomputed against the
  // current clock (otherwise the window would be frozen at first render).
  const [refreshNonce, setRefreshNonce] = useState(0);

  const since = useMemo(() => {
    const minutes = RANGE_OPTIONS.find(option => option.key === rangeKey)?.minutes ?? null;
    if (minutes == null) return null;
    return new Date(Date.now() - minutes * 60_000).toISOString();
    // refreshNonce is an intentional dependency: pressing Refresh re-evaluates
    // the window against the current time.
  }, [rangeKey, refreshNonce]);

  const filterInput = useMemo(
    () => ({
      ...(action !== ALL ? { actions: [action] } : {}),
      ...(phase !== ALL ? { phases: [phase] } : {}),
      ...(account.trim() !== '' ? { account: account.trim() } : {}),
      ...(since != null ? { since } : {}),
      ...(minDurationMs > 0 ? { minDurationMs } : {})
    }),
    [action, phase, account, since, minDurationMs]
  );

  const { data, pagination, loading, refetch } = useOffsetPaginatedQuery(query, {
    variables: page => ({ input: { page, ...filterInput } }),
    total: data => data.adminLogMany.total
  });

  const items = data?.adminLogMany.items ?? [];

  const onRefresh = () => {
    setRefreshNonce(value => value + 1);
    void refetch();
  };

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Logs"
        description="Activity feed of every admin action — newest first."
        primaryAction={
          <Button variant="secondary" onClick={onRefresh}>
            Refresh
          </Button>
        }
      />

      <Stack
        direction="row"
        spacing={4}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 5, alignItems: 'flex-start' }}
      >
        <Box sx={{ width: 200 }}>
          <SelectInput
            label="Time range"
            value={rangeKey}
            onChange={setRangeKey}
            options={rangeOptions}
          />
        </Box>

        <Box sx={{ width: 230 }}>
          <SelectInput label="Action" value={action} onChange={setAction} options={actionOptions} />
        </Box>

        <Box sx={{ width: 180 }}>
          <SelectInput label="Phase" value={phase} onChange={setPhase} options={phaseOptions} />
        </Box>

        <Box sx={{ width: 230 }}>
          <Input
            label="Account (id or alias)"
            value={accountDraft}
            placeholder="Filter, then press Enter"
            onChange={event => setAccountDraft(event.target.value)}
            onBlur={() => setAccount(accountDraft)}
            onKeyDown={event => {
              if (event.key === 'Enter') setAccount(accountDraft);
            }}
          />
        </Box>

        <Box sx={{ width: 200 }}>
          <SelectInput
            label="Min duration"
            value={minDurationMs}
            onChange={setMinDurationMs}
            options={minDurationOptions}
          />
        </Box>
      </Stack>

      <Table
        items={loading ? undefined : items}
        pagination={pagination}
        rowKey={log => log.id}
        columns={[
          {
            label: 'Timestamp',
            render: log => (
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {new Date(log.createdAt).toLocaleString()}
              </Typography>
            )
          },
          {
            label: 'Action',
            render: log => (
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {ACTION_LABEL[log.action] ?? log.action}
              </Typography>
            )
          },
          {
            label: 'Phase',
            render: log => <PhaseLabel phase={log.phase} />
          },
          {
            label: 'Message',
            render: log => (
              <Typography variant="body2" sx={{ maxWidth: 480, overflowWrap: 'anywhere' }}>
                {log.message}
              </Typography>
            )
          },
          {
            label: 'Actor',
            render: log => <Typography variant="body2">{log.actorEmail ?? '—'}</Typography>
          },
          {
            label: 'Account',
            render: log => (
              <Tooltip
                title={log.accountId ?? ''}
                arrow
                disableHoverListener={log.accountId == null}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                >
                  {log.accountAlias ?? log.accountId ?? '—'}
                </Typography>
              </Tooltip>
            )
          },
          {
            label: 'Duration',
            align: 'right',
            render: log => (
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {formatDuration(log.durationMs)}
              </Typography>
            )
          }
        ]}
        noDataPlaceholder="No log entries match these filters."
      />
    </MainWrapper>
  );
}

function PhaseLabel({ phase }: { readonly phase: AdminAuditPhase }) {
  const theme = useTheme();
  const color =
    phase === 'succeeded'
      ? theme.brandPalette.foreground.success
      : phase === 'failed'
        ? theme.brandPalette.foreground.negative
        : theme.brandPalette.foreground.neutralFaded;

  return (
    <Typography variant="body2" sx={{ color, fontWeight: 600, textTransform: 'capitalize' }}>
      {phase}
    </Typography>
  );
}

// Compact human-readable duration. Null (triggered rows / not measured) → "—".
function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes}m ${remainder}s`;
}

const query = gql<AdminLogManyGQL>`
  query AdminLogMany($input: AdminLogManyInput) {
    adminLogMany(input: $input) {
      total
      items {
        id
        action
        phase
        message
        actorEmail
        accountId
        accountAlias
        durationMs
        jobId
        createdAt
      }
    }
  }
`;
